import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Plus, TriangleAlert } from "lucide-react";

import { PageRouteHeadline } from "@/components/Layout/PageRouteHeadline";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useActiveBrokerAccount } from "@/contexts/ActiveBrokerAccountContext";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { isWorkspaceEnabledForUser } from "@/utils/workspaceFeatureFlag";
import { listBacktests } from "../../../services/backtestsApi";
import { listReportStrategies, listReports } from "../../../services/reportsApi";
import { listWorkspaces, updateWorkspace, deleteWorkspace } from "../../../services/workspacesApi";
import { toastWorkspaceApiError } from "../../../utils/workspaceApiErrors";
import { deleteFailedGeneratedReport } from "../../../utils/deleteFailedGeneratedReport";
import { subscribeReportReady } from "../../../utils/reportReadyEvents";
import { toast } from "sonner";
import type { Backtest } from "../../../types/backtests";
import type { Report, ReportStrategyResponse } from "../../../types/reports";
import type { WorkspaceDetail, WorkspaceSummary } from "../../../types/workspace";
import {
  StatisticsEmptyState,
  statisticsPageMinHeight,
} from "../Statistics/components/statisticsEmptyState";
import { statisticsSidebarSurfaceCardClassName } from "../Statistics/components/statisticsShadcnCardShell";
import { CreateStrategyDialog } from "../Strategies/CreateStrategyDialog";
import { WorkspaceHubWorkspacesCarousel } from "./WorkspaceHubWorkspacesCarousel";
import { WorkspaceCreateDialog } from "./WorkspaceCreateDialog";
import { WorkspaceErrorBoundary } from "./WorkspaceErrorBoundary";
import { WorkspaceGenerateReportDialog } from "./WorkspaceGenerateReportDialog";
import { WorkspaceHubEntitiesBoard } from "./WorkspaceHubEntitiesBoard";
import { ensureStrategyLinkedToAccount } from "./workspaceAttachStrategyAccount";
import { WorkspaceSimulatorDialog } from "./WorkspaceSimulatorDialog";
import { useWorkspaceAccountScope } from "./useWorkspaceAccountScope";
import { useWorkspaceCanvasPreviews } from "./useWorkspaceCanvasPreviews";
import { workspaceCanvasPath, type WorkspaceHubNavState } from "./workspaceRoutes";
import type { StrategyDetailLocationState } from "../Strategies/strategyDetailUtils";
import type { ReportDetailLocationState } from "../Reports/ReportDetail";
import { getReportDisplayTitle } from "../../../utils/reportWindowLabel";

/** Same shell as Reports / Simulator list cards — outer stroke via globals.css. */
const HUB_CARD_CLASS = cn(
  "flex w-full min-w-0 flex-col gap-4 overflow-x-auto rounded-[6px] border border-solid border-border bg-card p-4 text-card-foreground shadow-none",
  statisticsSidebarSurfaceCardClassName,
  "xl:overflow-x-hidden"
);

const HUB_SECTION_TITLE_CLASS =
  "font-[family-name:var(--font-primary)] text-base font-medium text-[color:var(--brand-tropical-surf)]";

const HUB_META_CLASS =
  "font-[family-name:var(--font-primary)] text-sm text-gray-500 dark:text-sidebar-foreground";

const FETCH_LIMIT = 100;
const HUB_REPORT_DISMISS_MS = 340;

function isHubOptimisticReportId(id: string): boolean {
  return String(id).startsWith("pending-");
}

function isFailedReportStatus(status: Report["status"] | string | null | undefined): boolean {
  return String(status ?? "").toLowerCase() === "failed";
}

function withoutFailedReports(reports: Report[]): Report[] {
  return reports.filter((report) => !isFailedReportStatus(report.status));
}

function mergeHubReportsWithOptimistic(
  serverReports: Report[],
  previous: Report[]
): Report[] {
  const cleanServer = withoutFailedReports(serverReports);
  const pending = previous.filter((r) => isHubOptimisticReportId(r.id));
  if (pending.length === 0) return cleanServer;

  const previousRealIds = new Set(
    previous.filter((r) => !isHubOptimisticReportId(r.id)).map((r) => r.id)
  );
  // If the API already returned the new processing row, drop the optimistic placeholder.
  const serverAdoptedPending = cleanServer.some(
    (r) =>
      (r.status === "processing" || r.status === "draft") && !previousRealIds.has(r.id)
  );
  if (serverAdoptedPending) return cleanServer;

  const serverIds = new Set(cleanServer.map((r) => r.id));
  return [...pending.filter((p) => !serverIds.has(p.id)), ...cleanServer];
}

export default function WorkspaceHubPage(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const shadcnInset = useShadcnInsetChrome();
  const pageMinHeight = statisticsPageMinHeight(shadcnInset);
  const { scope, ready, loading: accountLoading } = useWorkspaceAccountScope();
  const { accounts, setActiveTradeAccount } = useActiveBrokerAccount();
  const { isAdmin, isLoading: flagsLoading } = useFeatureFlags();

  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [strategies, setStrategies] = useState<ReportStrategyResponse[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [backtests, setBacktests] = useState<Backtest[]>([]);

  const [workspacesLoading, setWorkspacesLoading] = useState(true);
  const [hubError, setHubError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createStrategyOpen, setCreateStrategyOpen] = useState(false);
  const [createReportOpen, setCreateReportOpen] = useState(false);
  const [createSimulationOpen, setCreateSimulationOpen] = useState(false);
  const [cardMutatingId, setCardMutatingId] = useState<string | null>(null);

  /** Deep link: `/workspace?create=report&local_start_date=…&local_end_date=…` */
  useEffect(() => {
    const params = new URLSearchParams(
      location.search.startsWith("?") ? location.search.slice(1) : location.search
    );
    if (params.get("create") !== "report") return;

    const brokerCode = params.get("broker_account_code")?.trim() || "";
    // Wait for accounts before switching + clearing the deep-link flag.
    if (brokerCode && accounts.length === 0) {
      setCreateReportOpen(true);
      return;
    }

    if (brokerCode) {
      const acc = accounts.find((a) => a.accountCode === brokerCode);
      if (acc) {
        void setActiveTradeAccount(acc.brokerName, acc.accountCode);
      }
    }
    setCreateReportOpen(true);

    // Drop `create` so closing the dialog (or account refresh) does not reopen it.
    // Keep date/account params so the wizard can still prefill from the URL.
    params.delete("create");
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : "",
    });
  }, [accounts, history, location.pathname, location.search, setActiveTradeAccount]);

  const loadSeq = useRef(0);
  const pendingHubReportIdRef = useRef<string | null>(null);
  const dismissReportTimersRef = useRef<Map<string, number>>(new Map());
  const [dismissingReportIds, setDismissingReportIds] = useState<Set<string>>(
    () => new Set()
  );

  const fadeOutHubReport = useCallback(
    (reportId: string, options?: { deleteRemote?: boolean }) => {
      const id = reportId.trim();
      if (!id) return;

      setDismissingReportIds((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });

      const existing = dismissReportTimersRef.current.get(id);
      if (existing != null) window.clearTimeout(existing);

      const timer = window.setTimeout(() => {
        dismissReportTimersRef.current.delete(id);
        setReports((prev) => prev.filter((report) => report.id !== id));
        setDismissingReportIds((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        if (options?.deleteRemote !== false && !isHubOptimisticReportId(id)) {
          void deleteFailedGeneratedReport(id);
        }
      }, HUB_REPORT_DISMISS_MS);
      dismissReportTimersRef.current.set(id, timer);
    },
    []
  );

  const ingestServerReports = useCallback(
    (serverReports: Report[]) => {
      const failed = serverReports.filter((report) => isFailedReportStatus(report.status));
      const clean = withoutFailedReports(serverReports);

      setReports((prev) => {
        const visibleFailedIds = failed
          .filter((report) => prev.some((row) => row.id === report.id))
          .map((report) => report.id);

        queueMicrotask(() => {
          for (const id of visibleFailedIds) {
            fadeOutHubReport(id, { deleteRemote: true });
          }
          for (const report of failed) {
            if (!visibleFailedIds.includes(report.id)) {
              void deleteFailedGeneratedReport(report.id);
            }
          }
        });

        return mergeHubReportsWithOptimistic(clean, prev);
      });
    },
    [fadeOutHubReport]
  );

  const workspaceEnabled = !flagsLoading && isWorkspaceEnabledForUser(isAdmin);

  const newWorkspaceButton = useMemo(
    () => (
      <Button
        type="button"
        variant="gradient"
        size="sm"
        data-font="primary"
        data-workspace-new-button=""
        className="shrink-0"
        onClick={() => setCreateOpen(true)}
      >
        <Plus className="size-4" aria-hidden />
        <span className="normal-case tracking-normal">New workspace</span>
      </Button>
    ),
    []
  );

  const loadHub = useCallback(async () => {
    if (!scope) return;
    const seq = ++loadSeq.current;
    setHubError(null);
    setWorkspacesLoading(true);

    try {
      const [workspaceResp, strategiesResp, reportsResp, backtestsResp] = await Promise.all([
        listWorkspaces(scope),
        listReportStrategies({
          include_archived: false,
          limit: FETCH_LIMIT,
          offset: 0,
          broker_account_code: scope.brokerAccountCode,
          broker_name: scope.brokerName,
        }),
        listReports({
          broker_account_code: scope.brokerAccountCode,
          limit: FETCH_LIMIT,
          offset: 0,
        }),
        listBacktests({
          broker_account_code: scope.brokerAccountCode,
          limit: FETCH_LIMIT,
          offset: 0,
          include_result: true,
          include_snapshot: false,
        }),
      ]);

      if (seq !== loadSeq.current) return;

      setWorkspaces(workspaceResp.workspaces ?? []);
      setStrategies(
        (strategiesResp?.strategies ?? []).filter((strategy) => !strategy.is_archived)
      );
      ingestServerReports(reportsResp?.reports ?? []);
      setBacktests(backtestsResp?.backtests ?? []);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setWorkspaces([]);
      setStrategies([]);
      setReports([]);
      setBacktests([]);
      setHubError(err instanceof Error ? err.message : "Failed to load workspace hub.");
    } finally {
      if (seq !== loadSeq.current) return;
      setWorkspacesLoading(false);
    }
  }, [ingestServerReports, scope]);

  const refreshHubReports = useCallback(async () => {
    if (!scope) return;
    try {
      const reportsResp = await listReports({
        broker_account_code: scope.brokerAccountCode,
        limit: FETCH_LIMIT,
        offset: 0,
      });
      ingestServerReports(reportsResp?.reports ?? []);
    } catch {
      // Soft poll — keep existing cards if a refresh fails.
    }
  }, [ingestServerReports, scope]);

  useEffect(() => {
    if (!ready || !scope) return;
    void loadHub();
  }, [loadHub, ready, scope]);

  useEffect(() => {
    return () => {
      for (const timer of Array.from(dismissReportTimersRef.current.values())) {
        window.clearTimeout(timer);
      }
      dismissReportTimersRef.current.clear();
    };
  }, []);

  const hasProcessingHubReport = useMemo(
    () =>
      reports.some(
        (report) =>
          isHubOptimisticReportId(report.id) || report.status === "processing"
      ),
    [reports]
  );

  useEffect(() => {
    if (!hasProcessingHubReport || !scope) return;
    const timer = window.setInterval(() => {
      void refreshHubReports();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [hasProcessingHubReport, refreshHubReports, scope]);

  useEffect(() => {
    return subscribeReportReady((event) => {
      const reportId = event.reportId?.trim();
      if (!reportId) return;
      if (isFailedReportStatus(event.status)) {
        fadeOutHubReport(reportId, { deleteRemote: true });
        return;
      }
      void refreshHubReports();
    });
  }, [fadeOutHubReport, refreshHubReports]);

  const openWorkspace = useCallback(
    (workspace: WorkspaceSummary | WorkspaceDetail) => {
      history.push(workspaceCanvasPath(workspace.id));
    },
    [history]
  );

  const sortedWorkspaces = useMemo(() => {
    return [...workspaces].sort((a, b) => {
      const aTime = Date.parse(a.updatedAt ?? "") || 0;
      const bTime = Date.parse(b.updatedAt ?? "") || 0;
      return bTime - aTime;
    });
  }, [workspaces]);

  const { previews, seedPreview } = useWorkspaceCanvasPreviews(scope, sortedWorkspaces);

  const handleWorkspaceCreated = useCallback(
    (workspace: WorkspaceDetail) => {
      seedPreview(workspace);
      setWorkspaces((prev) => {
        const summary: WorkspaceSummary = {
          id: workspace.id,
          name: workspace.name,
          brokerName: workspace.brokerName,
          brokerAccountCode: workspace.brokerAccountCode,
          updatedAt: workspace.updatedAt ?? new Date().toISOString(),
          createdAt: workspace.createdAt,
          nodeCount: workspace.nodes?.length,
        };
        return [summary, ...prev.filter((w) => w.id !== workspace.id)];
      });
      void loadHub();
      openWorkspace(workspace);
    },
    [loadHub, openWorkspace, seedPreview]
  );

  const handleWorkspaceRename = useCallback(
    async (workspace: WorkspaceSummary, name: string) => {
      if (!scope) return;
      setCardMutatingId(workspace.id);
      try {
        const updated = await updateWorkspace(scope, workspace.id, { name });
        setWorkspaces((prev) =>
          prev.map((w) =>
            w.id === workspace.id
              ? {
                  ...w,
                  name: updated.name,
                  updatedAt: updated.updatedAt ?? w.updatedAt,
                }
              : w
          )
        );
        toast.success("Workspace renamed");
      } catch (err) {
        toastWorkspaceApiError(err, "Failed to rename workspace");
        throw err;
      } finally {
        setCardMutatingId(null);
      }
    },
    [scope]
  );

  const handleWorkspaceDelete = useCallback(
    async (workspace: WorkspaceSummary) => {
      if (!scope) return;
      setCardMutatingId(workspace.id);
      try {
        await deleteWorkspace(scope, workspace.id);
        setWorkspaces((prev) => prev.filter((w) => w.id !== workspace.id));
        toast.success("Workspace deleted");
      } catch (err) {
        toastWorkspaceApiError(err, "Failed to delete workspace");
        throw err;
      } finally {
        setCardMutatingId(null);
      }
    },
    [scope]
  );

  if (!flagsLoading && !isWorkspaceEnabledForUser(isAdmin)) {
    return (
      <div
        data-workspace-hub-page=""
        className="flex flex-col items-center justify-center"
        style={{ minHeight: pageMinHeight }}
      >
        <StatisticsEmptyState
          variant="no-data"
          title="Workspace not available"
          description="Workspace is not enabled for your account yet."
        />
      </div>
    );
  }

  if (accountLoading || flagsLoading) {
    return (
      <div
        data-workspace-hub-page=""
        className="flex flex-col items-center justify-center"
        style={{ minHeight: pageMinHeight }}
      >
        <StatisticsEmptyState
          variant="loading"
          title="Loading account…"
          description="Preparing workspace hub."
        />
      </div>
    );
  }

  if (!ready || !scope) {
    return (
      <div
        data-workspace-hub-page=""
        className="flex flex-col items-center justify-center"
        style={{ minHeight: pageMinHeight }}
      >
        <StatisticsEmptyState
          variant="no-data"
          title="Select an account"
          description="Choose a broker account to view workspaces and linked entities."
        />
      </div>
    );
  }

  return (
    <WorkspaceErrorBoundary onReset={() => void loadHub()}>
      <div
        data-workspace-hub-page=""
        className={cn(
          "flex w-full max-w-full flex-col gap-4",
          // Shell already applies `p-4`; only pad when outside inset chrome.
          !shadcnInset && "px-4 py-4 xl:px-6"
        )}
        style={{ minHeight: pageMinHeight }}
      >
      {!shadcnInset ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <PageRouteHeadline>Workspace</PageRouteHeadline>
        </div>
      ) : null}

      {hubError ? (
        <Alert variant="error">
          <TriangleAlert />
          <AlertDescription>{hubError}</AlertDescription>
        </Alert>
      ) : null}

      <section data-workspace-hub-cards="" className={HUB_CARD_CLASS}>
        <div className="flex items-center justify-between gap-3">
          <h2 className={HUB_SECTION_TITLE_CLASS}>Workspaces</h2>
          <div className="flex items-center gap-3">
            <span className={HUB_META_CLASS}>
              {workspacesLoading
                ? null
                : `${sortedWorkspaces.length} workspace${sortedWorkspaces.length === 1 ? "" : "s"}`}
            </span>
            {!workspacesLoading && sortedWorkspaces.length > 0
              ? newWorkspaceButton
              : null}
          </div>
        </div>

        {workspacesLoading ? (
          <StatisticsEmptyState
            variant="loading"
            title="Loading workspaces…"
            description="Fetching canvases for this account."
          />
        ) : sortedWorkspaces.length === 0 ? (
          <StatisticsEmptyState
            variant="no-data"
            title="No workspaces yet"
            description="Create one to start building your pipeline on the canvas."
            action={newWorkspaceButton}
          />
        ) : (
          <WorkspaceHubWorkspacesCarousel
            workspaces={sortedWorkspaces}
            previews={previews}
            cardMutatingId={cardMutatingId}
            onOpen={openWorkspace}
            onRename={handleWorkspaceRename}
            onDelete={handleWorkspaceDelete}
          />
        )}
      </section>

      <section data-workspace-hub-entities="" className={HUB_CARD_CLASS}>
        <WorkspaceHubEntitiesBoard
          strategies={strategies}
          reports={reports}
          backtests={backtests}
          loading={workspacesLoading}
          dismissingReportIds={dismissingReportIds}
          onOpenStrategy={(strategy) =>
            history.push({
              pathname: `/strategies/${strategy.id}`,
              state: {
                fromWorkspace: true,
                strategyDisplayName: strategy.name ?? undefined,
              } satisfies StrategyDetailLocationState,
            })
          }
          onOpenReport={(report) =>
            history.push({
              pathname: `/reports/${report.id}`,
              state: {
                fromWorkspace: true,
                reportDisplayTitle: getReportDisplayTitle(report),
              } satisfies ReportDetailLocationState,
            })
          }
          onOpenSimulation={(backtest) =>
            history.push({
              pathname: `/simulator/runs/${backtest.id}`,
              state: { fromWorkspace: true } satisfies WorkspaceHubNavState,
            })
          }
          onOpenStrategyById={(strategyId) =>
            history.push({
              pathname: `/strategies/${strategyId}`,
              state: { fromWorkspace: true } satisfies StrategyDetailLocationState,
            })
          }
          onOpenReportById={(reportId) =>
            history.push({
              pathname: `/reports/${reportId}`,
              state: { fromWorkspace: true } satisfies ReportDetailLocationState,
            })
          }
          onCreateStrategy={() => setCreateStrategyOpen(true)}
          onCreateReport={() => setCreateReportOpen(true)}
          onCreateSimulation={() => setCreateSimulationOpen(true)}
        />
      </section>

      <WorkspaceCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        scope={scope}
        onCreated={handleWorkspaceCreated}
      />
      <CreateStrategyDialog
        open={createStrategyOpen}
        onOpenChange={setCreateStrategyOpen}
        brokerAccountCode={scope?.brokerAccountCode}
        brokerName={scope?.brokerName}
        existingAttach={
          scope
            ? {
                brokerAccountCode: scope.brokerAccountCode,
                excludeEntityIds: strategies.map((strategy) => strategy.id),
                strategyAlreadyTakenMessage:
                  "All matching strategies are already linked to this account.",
                onAttach: async (strategyId) => {
                  try {
                    const updated = await ensureStrategyLinkedToAccount(strategyId, {
                      brokerName: scope.brokerName,
                      brokerAccountCode: scope.brokerAccountCode,
                    });
                    setStrategies((prev) => [
                      updated,
                      ...prev.filter((s) => s.id !== updated.id),
                    ]);
                    toast.success("Strategy linked to this account", {
                      description:
                        "Open a workspace canvas and use Add existing strategy to place it.",
                    });
                  } catch (err) {
                    toastWorkspaceApiError(err, "Failed to link strategy");
                    throw err;
                  }
                },
              }
            : undefined
        }
        onCreated={(created) => {
          setStrategies((prev) => [created, ...prev.filter((s) => s.id !== created.id)]);
          history.push({
            pathname: `/strategies/${created.id}`,
            state: {
              fromWorkspace: true,
              strategyDisplayName: created.name ?? undefined,
            } satisfies StrategyDetailLocationState,
          });
        }}
      />
      {scope ? (
        <WorkspaceGenerateReportDialog
          open={createReportOpen}
          onOpenChange={setCreateReportOpen}
          lockBrokerAccountCode={scope.brokerAccountCode}
          onGenerateStarted={(preview) => {
            const pendingId = `pending-${Date.now()}`;
            pendingHubReportIdRef.current = pendingId;
            const optimistic: Report = {
              id: pendingId,
              title: preview.title,
              status: "processing",
              created_at: new Date().toISOString(),
              broker_account_code: scope.brokerAccountCode,
            };
            setReports((prev) => [
              optimistic,
              ...prev.filter((r) => !isHubOptimisticReportId(r.id)),
            ]);
          }}
          onGenerateFailed={() => {
            const pendingId = pendingHubReportIdRef.current;
            pendingHubReportIdRef.current = null;
            if (!pendingId) return;
            fadeOutHubReport(pendingId, { deleteRemote: false });
          }}
          onReportGenerated={({ report, accepted }) => {
            const pendingId = pendingHubReportIdRef.current;
            pendingHubReportIdRef.current = null;
            if (isFailedReportStatus(report.status)) {
              if (pendingId) {
                setReports((prev) => prev.filter((r) => r.id !== pendingId));
              }
              fadeOutHubReport(report.id, { deleteRemote: true });
              return;
            }
            const nextStatus =
              accepted || report.status === "processing" || report.status === "draft"
                ? "processing"
                : report.status;
            setReports((prev) => {
              const withoutPending = prev.filter(
                (r) => r.id !== pendingId && r.id !== report.id
              );
              return [{ ...report, status: nextStatus ?? report.status }, ...withoutPending];
            });
            toast.success(
              accepted || nextStatus === "processing" ? "Report queued" : "Report created",
              {
                description:
                  accepted || nextStatus === "processing"
                    ? "Generating in the Reports lane — this card will update when ready."
                    : undefined,
              }
            );
          }}
        />
      ) : null}
      {scope ? (
        <WorkspaceSimulatorDialog
          open={createSimulationOpen}
          onOpenChange={setCreateSimulationOpen}
          scope={scope}
          lockBrokerAccountCode={scope.brokerAccountCode}
          resultsLocationState={{ fromWorkspace: true } satisfies WorkspaceHubNavState}
          onSimulationSaved={() => {
            void (async () => {
              try {
                const backtestsResp = await listBacktests({
                  broker_account_code: scope.brokerAccountCode,
                  limit: FETCH_LIMIT,
                  offset: 0,
                  include_result: true,
                  include_snapshot: false,
                });
                setBacktests(backtestsResp?.backtests ?? []);
              } catch {
                // Soft refresh — Generate usually routes to results first.
              }
            })();
          }}
        />
      ) : null}
      </div>
    </WorkspaceErrorBoundary>
  );
}
